import { FleetService } from './../shared/generated/backend/api/fleet.service';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { ProjectLoadunitDto, BranchService } from '../shared/generated/backend';
import { RouteMode } from 'devextreme/ui/map';
import { HttpClient } from '@angular/common/http';

export interface Marker {
  location: string | [number, number];
  color: string;
  tooltip: Tooltip;
  iconSrc: string;
  height: number;
  address?: string;
}

export interface Tooltip {
  isShown: boolean;
  text: string;
}

export interface Route {
  weight: number;
  color: string;
  opacity: number;
  mode: RouteMode;
  locations: (string | [number, number])[];
}

export interface RouteDistanceInfo {
  start: string;
  end: string;
  distance: number | null;
  duration: number | null;
  last?: string;
}

export interface AzureGeocodeResult {
  results: GeocodeResult[];
}

export interface GeocodeResult {
  score: number;
  address: {
    countryCode: string;
    freeformAddress: string;
  };
  position: {
    lat: number;
    lon: number;
  };
}

export interface AzureRouteResponse {
  routes: RouteSummary[];
}

export interface RouteSummary {
  summary: {
    lengthInMeters: number;
    travelTimeInSeconds: number;
  };
  legs: RouteLeg[];
}

export interface RouteLeg {
  summary: {
    lengthInMeters: number;
    travelTimeInSeconds: number;
  };
}

@Injectable({
  providedIn: 'root',
})
export class MapService {
  private markersSubject = new BehaviorSubject<Marker[]>([]);
  private routesSubject = new BehaviorSubject<Route[]>([]);
  private routeDistancesSubject = new BehaviorSubject<number[]>([]);
  private markersSubjectDistance = new BehaviorSubject<Marker[]>([]);
  private homeRoute = new BehaviorSubject<boolean>(true);
  private isProcessingSubject = new BehaviorSubject<boolean>(false);
  isProcessing$ = this.isProcessingSubject.asObservable();

  markers$ = this.markersSubject.asObservable();
  routes$ = this.routesSubject.asObservable();
  routeDistances$ = this.routeDistancesSubject.asObservable();
  homeRoute$ = this.homeRoute.asObservable();

  private autoAdjustSubject = new BehaviorSubject<boolean>(false);
  autoAdjust$ = this.autoAdjustSubject.asObservable();
  setAutoAdjust(v: boolean) {
    this.autoAdjustSubject.next(v);
  }

  private mapReadySubject = new BehaviorSubject<boolean>(false);
  mapReady$ = this.mapReadySubject.asObservable();
  private readySeqSubject = new BehaviorSubject<number>(0);
  readySeq$ = this.readySeqSubject.asObservable();

  projectNo!: number;
  isProcessing = false;
  updateRequested = false;
  updateRequestedLoadunits: ProjectLoadunitDto[] = [];
  private currentRoutes: Route[] = [];
  private geocodeCache = new Map<string, string>();
  private readonly MAX_CACHE_SIZE = 300;
  private readonly EUROPEAN_COUNTRIES = [
    'AL',
    'AD',
    'AT',
    'BE',
    'BA',
    'BG',
    'HR',
    'CY',
    'CZ',
    'DK',
    'EE',
    'FI',
    'FR',
    'DE',
    'GR',
    'HU',
    'IS',
    'IE',
    'IT',
    'LV',
    'LI',
    'LT',
    'LU',
    'MK',
    'MT',
    'MD',
    'MC',
    'ME',
    'NL',
    'NO',
    'PL',
    'PT',
    'RO',
    'RS',
    'SK',
    'SI',
    'ES',
    'SE',
    'CH',
    'UA',
    'GB',
    'VA',
  ] as const;

  private readonly CACHE_KEY_SEPARATOR = '|';
  private readonly MAX_PARALLEL_REQUESTS = 5;
  private readonly ROUTE_WEIGHT = 6;
  private readonly ROUTE_COLOR = 'blue';
  private readonly ROUTE_OPACITY = 0.5;
  private readonly ROUTE_MODE: RouteMode = 'driving';
  private readonly MARKER_HEIGHT = 5;
  private readonly RENDER_DELAY_MS = 50;

  private branchService = inject(BranchService);
  private fleetService = inject(FleetService);
  private http = inject(HttpClient);

  setHomeRoute(val: boolean) {
    this.homeRoute.next(val);
  }
  get homeRouteValue(): boolean {
    return this.homeRoute.value;
  }
  private subscriptionKeyPromise?: Promise<string>;

  private context: {
    loadunits?: ProjectLoadunitDto[];
    projectNo?: number;
  } = {};

  //Push / refresh context once – call whenever data changes.
  setContext(loadunits: ProjectLoadunitDto[], projectNo: number) {
    this.context = { loadunits, projectNo };
  }

  private async getSubscriptionKey(): Promise<string> {
    if (!this.subscriptionKeyPromise) {
      this.subscriptionKeyPromise = firstValueFrom(this.fleetService.getMapsConnectionKey());
    }
    return this.subscriptionKeyPromise;
  }

  async updateMap(
    loadunits = this.context.loadunits!,
    projectNo = this.context.projectNo!,
    withHomeAddress = this.homeRouteValue,
    withDistanceInfo = false
  ): Promise<void> {
    if (this.isProcessing) {
      this.projectNo = projectNo;
      this.updateRequested = true;
      this.updateRequestedLoadunits = loadunits;
      return;
    }
    this.isProcessing = true;
    this.isProcessingSubject.next(true);
    this.mapReadySubject.next(false);

    const markers: Marker[] = [];
    const branchAddress = await firstValueFrom(this.branchService.getHomeAddress());
    const branchLocation = this.createLocationString(
      branchAddress.Street,
      branchAddress.City,
      branchAddress.PostalCode
    );

    // Pre-geocode branch location to avoid automatic geocoding by dx-map
    let branchCoordinate: string | [number, number] = branchLocation;
    if (branchLocation) {
      branchCoordinate = await this.resolveMarkerLocation(branchLocation);
      // Only add branch marker if we have valid coordinates or address
      if (typeof branchCoordinate === 'string' || (Array.isArray(branchCoordinate) && branchCoordinate.length === 2)) {
        markers.push(this.createMarker(branchLocation, branchCoordinate, 'blue', 'Niclen'));
      }
    }

    // Collect unique addresses with their country codes for batch geocoding
    const uniqueAddresses = new Map<string, string>();
    for (const [_, lu] of loadunits.entries()) {
      const location = this.createLocationString(lu.ActionStreet, lu.ActionCity, lu.ActionZipCode);
      if (location) {
        uniqueAddresses.set(location, lu.ActionCountryCode || '');
      }
    }

    // Batch geocode all unique addresses in parallel with throttling
    const addressCoordinates = new Map<string, string | [number, number]>();
    const addressEntries = Array.from(uniqueAddresses.entries());

    for (let i = 0; i < addressEntries.length; i += this.MAX_PARALLEL_REQUESTS) {
      const batch = addressEntries.slice(i, i + this.MAX_PARALLEL_REQUESTS);
      await Promise.all(
        batch.map(([addr, country]) =>
          this.resolveMarkerLocation(addr, country).then(coord => addressCoordinates.set(addr, coord))
        )
      );
    }

    for (const [index, lu] of loadunits.entries()) {
      const location = this.createLocationString(lu.ActionStreet, lu.ActionCity, lu.ActionZipCode);
      if (location) {
        const coord = addressCoordinates.get(location) || location;
        // Only add marker if we have valid coordinates or address
        if (typeof coord === 'string' || (Array.isArray(coord) && coord.length === 2)) {
          markers.push(this.createMarker(location, coord, 'red', `${index + 1}. ${lu.Customer}`));
        }
      }
    }

    // Add branch marker again if needed (for round trip)
    if (branchLocation) {
      if (typeof branchCoordinate === 'string' || (Array.isArray(branchCoordinate) && branchCoordinate.length === 2)) {
        markers.push(this.createMarker(branchLocation, branchCoordinate, 'blue', 'Niclen'));
      }
    }

    if (markers.length > 1) {
      const routeLocationsRaw = withHomeAddress
        ? markers.map(m => m.location)
        : markers.filter(m => m.color === 'red').map(m => m.location);

      // Filter out invalid locations (null, undefined, empty, or wrong array length)
      const validRouteLocations = routeLocationsRaw.filter(loc => {
        if (!loc) return false;
        if (typeof loc === 'string' && loc.trim() !== '') return true;
        if (Array.isArray(loc) && loc.length === 2 && !isNaN(Number(loc[0])) && !isNaN(Number(loc[1]))) return true;
        return false;
      });

      const filteredRouteLocations = this.filterConsecutiveDuplicateLocations(validRouteLocations);
      const markersLocation = withHomeAddress ? markers : markers.filter(m => m.color === 'red');
      this.markersSubject.next([...markersLocation]);

      if (filteredRouteLocations.length < 2) {
        console.info('Route skipped! Need at least two valid points', filteredRouteLocations);
        this.setRoutes([]);
      } else {
        this.setRoutes([this.createRoute(filteredRouteLocations)]);
      }
    }
    this.markersSubjectDistance.next([...markers]);

    if (withDistanceInfo) {
      await this.calculateRouteDistanceInfo().catch(err =>
        console.error('Error calculating route distance info:', err)
      );
    }
    await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));

    this.isProcessing = false;
    this.isProcessingSubject.next(false);
    this.readySeqSubject.next(this.readySeqSubject.value + 1);

    await new Promise(r => setTimeout(r, this.RENDER_DELAY_MS));

    this.mapReadySubject.next(true);
    if (this.updateRequested) {
      this.updateRequested = false;
      this.updateMap(this.updateRequestedLoadunits, projectNo, withHomeAddress, withDistanceInfo);
    }
  }

  private createLocationString(...parts: (string | null | undefined)[]): string {
    return parts.filter(part => part).join(',');
  }

  private createMarker(
    address: string,
    location: string | [number, number],
    color: string,
    tooltipText: string
  ): Marker {
    return {
      address,
      location,
      color,
      tooltip: { isShown: true, text: tooltipText },
      iconSrc: `https://maps.google.com/mapfiles/ms/icons/${color}.png`,
      height: this.MARKER_HEIGHT,
    };
  }

  private async resolveMarkerLocation(address: string, countryCode?: string): Promise<string | [number, number]> {
    const coordinate = await this.geocodeAddress(address, countryCode);
    if (!coordinate) {
      return address;
    }

    const [lat, lon] = coordinate.split(',').map(value => parseFloat(value));
    if (Number.isFinite(lat) && Number.isFinite(lon)) {
      return [lat, lon];
    }

    return address;
  }

  private setRoutes(routes: Route[]): void {
    if (this.areRoutesEqual(routes, this.currentRoutes)) {
      return;
    }

    this.currentRoutes = routes;
    this.routesSubject.next(routes);
  }

  private createRoute(locations: (string | [number, number])[]): Route {
    return {
      weight: this.ROUTE_WEIGHT,
      color: this.ROUTE_COLOR,
      opacity: this.ROUTE_OPACITY,
      mode: this.ROUTE_MODE,
      locations,
    };
  }

  private filterConsecutiveDuplicateLocations(
    locations: Array<string | [number, number]>
  ): Array<string | [number, number]> {
    return locations.filter((location, index, all) => {
      if (index === 0) {
        return true;
      }
      return !this.areLocationsEqual(location, all[index - 1]);
    });
  }

  private areLocationsEqual(a: string | [number, number], b: string | [number, number]): boolean {
    if (typeof a === 'string' && typeof b === 'string') {
      return this.normalizeLocationString(a) === this.normalizeLocationString(b);
    }
    if (Array.isArray(a) && Array.isArray(b)) {
      return a[0] === b[0] && a[1] === b[1];
    }
    if (typeof a === 'string' && Array.isArray(b)) {
      return this.normalizeLocationString(a) === `${b[0]},${b[1]}`;
    }
    if (Array.isArray(a) && typeof b === 'string') {
      return `${a[0]},${a[1]}` === this.normalizeLocationString(b);
    }
    return false;
  }

  private normalizeLocationString(location: string): string {
    return location
      .trim()
      .replace(/\s+/g, ' ')
      .split(',')
      .map(part => part.trim())
      .join(',');
  }

  private areRoutesEqual(a: Route[], b: Route[]): boolean {
    if (a.length !== b.length) {
      return false;
    }
    for (let i = 0; i < a.length; i++) {
      const routeA = a[i];
      const routeB = b[i];
      if (
        routeA.weight !== routeB.weight ||
        routeA.color !== routeB.color ||
        routeA.opacity !== routeB.opacity ||
        routeA.mode !== routeB.mode ||
        routeA.locations.length !== routeB.locations.length
      ) {
        return false;
      }
      for (let j = 0; j < routeA.locations.length; j++) {
        if (!this.areLocationsEqual(routeA.locations[j], routeB.locations[j])) {
          return false;
        }
      }
    }
    return true;
  }

  showTooltips(): void {
    const updatedMarkers = this.markersSubject.getValue().map(marker => ({
      ...marker,
      tooltip: { ...marker.tooltip, isShown: true },
    }));
    this.markersSubject.next(updatedMarkers);
  }

  async calculateRouteDistanceInfo(): Promise<void> {
    this.routeDistancesSubject.next([]);
    const distanceInfos = await this.getRouteDistanceInfos();
    this.routeDistancesSubject.next(distanceInfos.map(info => info.distance ?? 0));
  }

  async calculateRouteDistanceInfoWithMarkers(markers: Marker[]): Promise<void> {
    this.routeDistancesSubject.next([]);
    const distanceInfos = await this.buildRouteDistanceInfos(markers);
    this.routeDistancesSubject.next(distanceInfos.map(info => info.distance ?? 0));
  }

  private async geocodeAddress(address: string, countryCode?: string): Promise<string> {
    const normalizedAddress = address.trim().replace(/\s+/g, ' ').toLowerCase();
    if (!normalizedAddress) {
      return '';
    }

    const countrySet = countryCode ? countryCode : this.EUROPEAN_COUNTRIES.join(',');

    const queryVariants = this.getQueryVariants(address);

    for (let i = 0; i < queryVariants.length; i++) {
      const query = queryVariants[i];
      const normalizedQuery = query.trim().replace(/\s+/g, ' ').toLowerCase();
      const cacheKey = countryCode ? `${normalizedQuery}${this.CACHE_KEY_SEPARATOR}${countryCode}` : normalizedQuery;

      if (this.geocodeCache.has(cacheKey)) {
        return this.geocodeCache.get(cacheKey)!;
      }

      try {
        const response = await firstValueFrom(
          this.http.get<AzureGeocodeResult>(
            `https://atlas.microsoft.com/search/address/json?api-version=1.0&subscription-key=${encodeURIComponent(
              await this.getSubscriptionKey()
            )}&query=${encodeURIComponent(query)}&countrySet=${encodeURIComponent(countrySet)}`
          )
        );

        if (response.results?.length > 0) {
          const sortedResults = [...response.results].sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
          const bestResult = sortedResults[0];
          const bestScore = bestResult.score ?? 1;
          const resultCountryCode = bestResult.address?.countryCode;

          console.debug(`Geocode API returned ${response.results.length} results for "${query}" (variant ${i})`, {
            bestScore,
            bestCountry: resultCountryCode,
            allResults: response.results.map((r: GeocodeResult) => ({
              score: r.score,
              country: r.address?.countryCode,
              formatted: r.address?.freeformAddress,
            })),
          });

          const isValidCountry = countryCode
            ? resultCountryCode === countryCode
            : (this.EUROPEAN_COUNTRIES as readonly string[]).includes(resultCountryCode);

          if (isValidCountry) {
            if (bestScore < 0.5) {
              console.warn(
                `Low confidence geocode result for address: ${address}. Score: ${bestScore}, Country: ${resultCountryCode}`
              );
            }
            const coords = bestResult.position;
            const result = `${coords.lat},${coords.lon}`;
            this.cacheGeocodeResult(cacheKey, result);
            return result;
          }

          console.warn(
            `Geocode result rejected for address: ${address}. Expected: ${countryCode || 'EU'}, Got: ${resultCountryCode}, Score: ${bestScore}`
          );
        } else {
          console.debug(`No geocode results for variant ${i}: "${query}"`);
        }
      } catch (err) {
        console.error(`Error geocoding variant ${i}: "${query}"`, err);
      }
    }

    console.warn('No geocode results found for address after all variants', address);
    return '';
  }

  private cacheGeocodeResult(key: string, value: string): void {
    if (this.geocodeCache.size >= this.MAX_CACHE_SIZE) {
      const oldestKey = this.geocodeCache.keys().next().value;
      if (oldestKey !== undefined) {
        this.geocodeCache.delete(oldestKey);
      }
    }
    this.geocodeCache.set(key, value);
  }

  private getQueryVariants(address: string): string[] {
    return [
      address,
      address.replace(/,\s*/g, ', '),
      (() => {
        const parts = address.split(',');
        if (parts.length >= 2) {
          return parts[parts.length - 2].trim() + ', ' + parts[parts.length - 1].trim();
        }
        return address;
      })(),
      (() => {
        const addressParts = address.split(',');
        if (addressParts.length > 0) {
          const houseNumberPattern = /\s+\d+[a-zA-Z]?(-\d+[a-zA-Z]?)?\s*$/;
          return addressParts[0].replace(houseNumberPattern, '').trim();
        }
        return address;
      })(),
    ];
  }

  private async getPointDistance(start: string, end: string): Promise<RouteDistanceInfo> {
    if (!start || !end) {
      console.warn('Missing start or end coordinate – skip distance call', { start, end });
      return { start, end, distance: null, duration: null };
    }

    if (start === end) {
      return { start, end, distance: 0, duration: 0 };
    }

    const subscriptionKey = await this.getSubscriptionKey();
    const baseUrl = 'https://atlas.microsoft.com/route/directions/json?api-version=1.0';
    const url = `${baseUrl}&subscription-key=${subscriptionKey}&query=${start}:${end}`;

    try {
      const response = await firstValueFrom(this.http.get<AzureRouteResponse>(url));
      if (!response.routes?.length) {
        console.warn('No route found between the given points', { start, end });
        return { start, end, distance: null, duration: null };
      }

      const route = response.routes[0];
      return {
        start,
        end,
        distance: Math.floor(route.summary.lengthInMeters / 1000),
        duration: Math.floor(route.summary.travelTimeInSeconds / 60),
      };
    } catch (err) {
      console.error('Error calculating distance between points', { start, end, error: err });
      return { start, end, distance: null, duration: null };
    }
  }

  async getRouteDistanceInfos(): Promise<RouteDistanceInfo[]> {
    const markers = this.markersSubjectDistance.getValue();
    if (markers.length < 2) {
      console.warn('Not enough markers to calculate distances', { markerCount: markers.length });
      return [];
    }

    return this.buildRouteDistanceInfos(markers);
  }

  async buildRouteDistanceInfos(markers: Marker[]): Promise<RouteDistanceInfo[]> {
    const validLocations: Array<{ location: string; markerIndex: number; marker: Marker }> = [];
    for (let i = 0; i < markers.length; i++) {
      const marker = markers[i];
      const location = marker.location;
      let coordString: string | null = null;

      if (typeof location === 'string') {
        const normalized = this.normalizeLocationString(location);
        const parts = normalized.split(',');
        if (parts.length === 2 && !isNaN(parseFloat(parts[0])) && !isNaN(parseFloat(parts[1]))) {
          coordString = normalized;
        } else {
          const coordinate = await this.geocodeAddress(location);
          if (coordinate) {
            coordString = coordinate;
          } else {
            console.warn(`Could not geocode address for distance calculation: ${location}`);
          }
        }
      } else if (Array.isArray(location) && location.length === 2) {
        coordString = `${location[0]},${location[1]}`;
      }

      if (coordString) {
        validLocations.push({ location: coordString, markerIndex: i, marker });
      }
    }

    const locations = validLocations.map(l => l.location);

    if (locations.length < 2) {
      console.warn('Not enough valid locations for distance calculation', {
        markerCount: markers.length,
        validLocationCount: locations.length,
      });
      return [];
    }

    const multiPointResult = await this.getMultiPointRouteDistanceInfos(locations);
    if (multiPointResult.length > 0) {
      return multiPointResult;
    } else {
      console.info('Multi-point route calculation failed, falling back to segment-by-segment', { locations });
    }

    const routeSections: RouteDistanceInfo[] = [];

    for (let i = 0; i < locations.length - 1; i++) {
      const from = locations[i];
      const to = locations[i + 1];
      const part = await this.getPointDistance(from, to);
      if (part) {
        routeSections.push(part);
      } else {
        console.warn('Failed to calculate distance between points', { from, to, index: i });
      }
    }

    if (routeSections.length === 0) {
      console.error('All route sections failed to calculate distances');
      return [];
    }

    return this.buildDistanceInfos(routeSections);
  }

  private async getMultiPointRouteDistanceInfos(locations: string[]): Promise<RouteDistanceInfo[]> {
    try {
      const subscriptionKey = await this.getSubscriptionKey();
      const baseUrl = 'https://atlas.microsoft.com/route/directions/json?api-version=1.0';
      const query = encodeURIComponent(locations.join(':'));
      const url = `${baseUrl}&subscription-key=${subscriptionKey}&query=${query}`;

      const response = await firstValueFrom(this.http.get<AzureRouteResponse>(url));
      const route = response.routes?.[0];
      const legs = route?.legs;
      if (!Array.isArray(legs) || legs.length !== locations.length - 1) {
        console.warn('Multi-point route failed - legs mismatch', {
          expectedLegs: locations.length - 1,
          actualLegs: legs?.length,
          locations,
        });
        return [];
      }

      const routeSections = this.extractRouteSections(legs, locations);

      return this.buildDistanceInfos(routeSections);
    } catch (err) {
      console.error('Error in multi-point route calculation', { error: err, locations });
      return [];
    }
  }

  private extractRouteSections(legs: RouteLeg[], locations: string[]): RouteDistanceInfo[] {
    return legs.map((leg: RouteLeg, index: number) => ({
      start: locations[index],
      end: locations[index + 1],
      distance: Math.floor((leg?.summary?.lengthInMeters ?? 0) / 1000),
      duration: Math.floor((leg?.summary?.travelTimeInSeconds ?? 0) / 60),
    }));
  }

  private buildDistanceInfos(routeSections: RouteDistanceInfo[]): RouteDistanceInfo[] {
    const startToFirst = routeSections[0];
    const lastToEnd = routeSections[routeSections.length - 1];

    const totalDistance = routeSections.reduce((sum, section) => sum + (section.distance ?? 0), 0);
    const totalDuration = routeSections.reduce((sum, section) => sum + (section.duration ?? 0), 0);

    const start = startToFirst?.start ?? '';
    const end = lastToEnd?.end ?? '';
    const last = routeSections[routeSections.length - 2]?.end ?? '';

    const startToLast: RouteDistanceInfo = {
      start,
      end: last,
      last,
      distance: totalDistance - (lastToEnd?.distance ?? 0),
      duration: totalDuration - (lastToEnd?.duration ?? 0),
    };

    const totalRoute: RouteDistanceInfo = {
      start,
      end,
      distance: totalDistance,
      duration: totalDuration,
    };

    return [startToFirst, startToLast, lastToEnd, totalRoute];
  }
}
